import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Mail, MessageSquare, Send } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import AppLayout from '../components/layout/AppLayout';
import {
  createMessagingCampaign,
  fetchMessagingChannelSettings,
  fetchMessagingEligiblePlayers,
  quoteMessagingCampaign,
  type MessagingChannel,
  type MessagingChannelSettings,
  type MessagingEligiblePlayer,
  type MessagingQuote,
} from '../services/api';
import { PAGE } from '@kioskgaming/ui';

const BODY_MAX = 2000;
const SUBJECT_MAX = 200;

function formatUsd(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

export default function MessagesComposePage() {
  const navigate = useNavigate();
  const [channel, setChannel] = useState<MessagingChannel>('email');
  const [channelSettings, setChannelSettings] = useState<MessagingChannelSettings>({
    emailEnabled: true,
    smsEnabled: false,
  });
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [players, setPlayers] = useState<MessagingEligiblePlayer[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quote, setQuote] = useState<MessagingQuote | null>(null);
  const [loading, setLoading] = useState(true);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchMessagingEligiblePlayers(channel);
      if (res.success && res.data?.players) {
        setPlayers(res.data.players);
        setSelected(new Set());
      }
    } catch {
      toast.error('Could not load eligible Players');
    } finally {
      setLoading(false);
    }
  }, [channel]);

  useEffect(() => {
    void loadPlayers();
  }, [loadPlayers]);

  useEffect(() => {
    void fetchMessagingChannelSettings()
      .then((res) => {
        if (!res.success || !res.data) return;
        setChannelSettings(res.data);
        if (!res.data.emailEnabled && res.data.smsEnabled) setChannel('sms');
      })
      .catch(() => {
        // Keep the safe defaults: email enabled, SMS off until the provider is registered.
      });
  }, []);

  useEffect(() => {
    if (!selected.size || !body.trim() || (channel === 'email' && !subject.trim())) {
      setQuote(null);
      return;
    }
    const timer = setTimeout(async () => {
      setQuoting(true);
      try {
        const res = await quoteMessagingCampaign({
          channel,
          subject: channel === 'email' ? subject.trim() : undefined,
          body: body.trim(),
          playerIds: Array.from(selected),
        });
        if (res.success && res.data) setQuote(res.data);
      } catch {
        setQuote(null);
      } finally {
        setQuoting(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [channel, subject, body, selected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) {
      toast.error('Message body is required');
      return;
    }
    if (channel === 'email' && !subject.trim()) {
      toast.error('Email subject is required');
      return;
    }
    if (selected.size === 0) {
      toast.error('Select at least one Player');
      return;
    }
    if (quote && !quote.sufficientBalance) {
      toast.error('Insufficient USD wallet balance');
      return;
    }

    setSubmitting(true);
    try {
      const res = await createMessagingCampaign({
        channel,
        subject: channel === 'email' ? subject.trim() : undefined,
        body: body.trim(),
        playerIds: Array.from(selected),
      });
      if (res.success && res.data?.campaign?.id) {
        toast.success('Campaign queued');
        navigate(`/messages/${res.data.campaign.id}`);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to send campaign');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <PageLoadingIndicator />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-stone-900">{PAGE.emailSms}</h1>
          <p className="text-sm text-stone-600 mt-1">
            Paid email or SMS to verified Players. Fees debit your USD wallet.
          </p>
        </div>
        <Link to="/messages/history" className="text-sm font-medium text-emerald-700 hover:text-emerald-800">
          View history
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!channelSettings.emailEnabled}
            onClick={() => setChannel('email')}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${
              channel === 'email'
                ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                : 'border-stone-200 bg-white text-stone-700'
            } disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <Mail className="h-4 w-4" /> Email
          </button>
          <button
            type="button"
            disabled={!channelSettings.smsEnabled}
            onClick={() => setChannel('sms')}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium ${
              channel === 'sms'
                ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                : 'border-stone-200 bg-white text-stone-700'
            } disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <MessageSquare className="h-4 w-4" /> SMS
            {!channelSettings.smsEnabled && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
                Coming soon
              </span>
            )}
          </button>
        </div>

        {channel === 'email' && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
              className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
              maxLength={SUBJECT_MAX}
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Message</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            rows={8}
            className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm"
            maxLength={BODY_MAX}
          />
          <p className="text-xs text-stone-500 mt-1">
            {body.length}/{BODY_MAX}
            {channel === 'sms' && quote?.smsSegmentsPerMessage
              ? ` · ${quote.smsSegmentsPerMessage} SMS segment(s) per recipient`
              : null}
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-stone-800">Eligible Players ({players.length})</h2>
            <button
              type="button"
              className="text-xs text-emerald-700"
              onClick={() => setSelected(new Set(players.map((p) => p.id)))}
            >
              Select all
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto rounded-lg border border-stone-200 divide-y">
            {players.length === 0 ? (
              <p className="p-4 text-sm text-stone-500">No eligible Players for {channel}.</p>
            ) : (
              players.map((p) => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-stone-50">
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                  <span className="font-medium">{p.username || p.id.slice(0, 8)}</span>
                  <span className="text-stone-500 truncate">
                    {channel === 'email' ? p.email : p.phone}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>

        {quote && (
          <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm space-y-1">
            <p>
              Estimated cost: <strong>{formatUsd(quote.estimatedCostUsd)}</strong> ({quote.eligibleCount}{' '}
              recipient{quote.eligibleCount === 1 ? '' : 's'} × {formatUsd(quote.unitPriceUsd)}
              {channel === 'sms' ? '/segment' : ''})
            </p>
            <p>
              USD balance available: <strong>{formatUsd(quote.availableUsd)}</strong>
              {!quote.sufficientBalance && (
                <span className="text-red-600 ml-2">Insufficient balance</span>
              )}
            </p>
            {quote.ineligibleCount > 0 && (
              <p className="text-amber-700">{quote.ineligibleCount} selected Player(s) are not eligible.</p>
            )}
            {quoting && <p className="text-stone-500">Updating quote…</p>}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || quoting || (quote != null && !quote.sufficientBalance)}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {submitting ? 'Sending…' : 'Send & pay'}
        </button>
      </form>
    </AppLayout>
  );
}
